/**
 * Outlook-specific message types and schemas
 */

import { z } from "zod";
import { MessageBaseSchema } from "../message";

// ============================================================================
// OUTLOOK MESSAGE METADATA SCHEMAS
// ============================================================================

export const OutlookMessageMetadataSchema = z.object({
  categories: z.array(z.string()).optional(),
  flag: z
    .object({
      flagStatus: z.enum(["notFlagged", "flagged", "complete"]),
      dueDate: z.string().datetime().optional(),
    })
    .optional(),
  isDeliveryReceiptRequested: z.boolean().optional(),
  isReadReceiptRequested: z.boolean().optional(),
  isDraft: z.boolean().optional(),
  parentFolderId: z.string().optional(),
  changeKey: z.string().optional(),
  ccRecipients: z
    .array(
      z.object({
        emailAddress: z.object({
          name: z.string(),
          address: z.string().email(),
        }),
      })
    )
    .optional(),
  bccRecipients: z
    .array(
      z.object({
        emailAddress: z.object({
          name: z.string(),
          address: z.string().email(),
        }),
      })
    )
    .optional(),
  replyTo: z
    .array(
      z.object({
        emailAddress: z.object({
          name: z.string(),
          address: z.string().email(),
        }),
      })
    )
    .optional(),
}).strict();

export type OutlookMessageMetadata = z.infer<typeof OutlookMessageMetadataSchema>;

// ============================================================================
// OUTLOOK MESSAGE SCHEMAS
// ============================================================================

export const OutlookMessageSchema = MessageBaseSchema.extend({
  provider: z.literal("outlook-oauth"),
  bodyContentType: z.enum(["text", "html"]).optional(),
  isReminderOn: z.boolean().optional(),
  reminderMinutesBeforeStart: z.number().int().nonnegative().optional(),
  metadata: OutlookMessageMetadataSchema,
}).strict();

export type OutlookMessage = z.infer<typeof OutlookMessageSchema>;

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Type guard to check if a message is an Outlook message
 */
export function isOutlookMessage(message: any): message is OutlookMessage {
  return message?.provider === "outlook-oauth";
}

/**
 * Validate an Outlook message
 */
export function validateOutlookMessage(data: unknown): OutlookMessage {
  return OutlookMessageSchema.parse(data);
}
